import Image from "next/image";
import {
  Sparkles,
  Target,
  Shield,
  GitBranch,
  Database,
  Users,
  Handshake,
  Map,
  Megaphone,
  GraduationCap,
  Coins,
  Layers,
  type LucideIcon,
} from "lucide-react";

interface WhyDbiCard {
  id: string;
  icon?: string;
  title: string;
  description: string;
  note?: string;
  subheading?: string;
  subtext?: string;
}

interface WhyDbiCardsProps {
  cards: WhyDbiCard[];
  image?: string;
  imageAlt?: string;
  intro?: string;
  layout?: "grid" | "rows";
}

const iconMap: Record<string, LucideIcon> = {
  Sparkles,
  Target,
  Shield,
  GitBranch,
  Database,
  Users,
  Handshake,
  Map,
  Megaphone,
  GraduationCap,
  Coins,
  Layers,
};

function resolveIcon(name?: string): LucideIcon | null {
  if (!name) {
    return null;
  }
  const normalized = name
    .split(/[-_]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
  const Icon = iconMap[normalized];
  if (!Icon) {
    throw new Error(`WhyDbiCards: unsupported icon "${name}".`);
  }
  return Icon;
}

/**
 * WhyDbiCards
 *
 * Row grid used by `whyDbi`-style sections in service JSONs. Renders an
 * optional intro paragraph and image, then rows with an optional icon badge,
 * a colored title, description, an optional secondary block (`subheading` +
 * `subtext`) and an optional `note` styled as a proof/evidence line. The grid
 * spreads its rows across the full content width whatever the row count.
 */
export default function WhyDbiCards({
  cards,
  image,
  imageAlt,
  intro,
  layout = "grid",
}: WhyDbiCardsProps) {
  if (!Array.isArray(cards) || cards.length === 0) {
    throw new Error("WhyDbiCards: `cards` array is empty or invalid.");
  }

  return (
    <div className="why-dbi">
      {intro ? <p className="why-dbi-intro">{intro}</p> : null}
      {image ? (
        <div className="why-dbi-image">
          <Image
            src={image}
            alt={imageAlt ?? ""}
            width={1600}
            height={760}
            sizes="(min-width: 980px) 720px, 100vw"
          />
        </div>
      ) : null}
      <div className={layout === "rows" ? "why-dbi-grid why-dbi-grid-rows" : "why-dbi-grid"}>
        {cards.map((card) => {
          const Icon = resolveIcon(card.icon);
          return (
            <article className="why-dbi-card" key={card.id}>
              {Icon ? (
                <span className="why-dbi-ico" aria-hidden>
                  <Icon size={22} strokeWidth={1.75} />
                </span>
              ) : null}
              <div className="why-dbi-card-body">
                <h3>{card.title}</h3>
                <p>{card.description}</p>
                {card.subheading ? (
                  <div className="why-dbi-sub">
                    <h5>{card.subheading}</h5>
                    {card.subtext ? <p>{card.subtext}</p> : null}
                  </div>
                ) : null}
                {card.note ? <p className="why-dbi-note">{card.note}</p> : null}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
